import FormGroup from "legacy/FormGroup"
import NextButton from "legacy/boater-reservation-flow/NextButton"
import DockwaPlusCard from "legacy/boater-reservation-flow/price-estimate/dockwa-plus-sign-up/DockwaPlusCard"
import StripeElement from "legacy/credit-cards/StripeElement"
import { isset } from "legacy/helpers.es6"
import moment from "moment"
import PropTypes from "prop-types"
import React from "react"

import HorizontalRule from "src/components/LineItems/HorizontalRule"
import LineItems from "src/components/LineItems/LineItems"

/* eslint-disable camelcase */

class DockwaPlusPurchaseScreen extends React.Component {
  constructor(props) {
    super(props)

    const { productInformation, paymentMethods } = props

    const firstPaymentMethod = paymentMethods[0]
    const hasZipCode = firstPaymentMethod && firstPaymentMethod.metadata.zip

    const initialPaymentMethod = firstPaymentMethod
      ? firstPaymentMethod.stripePaymentMethodId
      : "add"

    this.state = {
      selectedPaymentMethod: initialPaymentMethod,
      zipCode: hasZipCode ? firstPaymentMethod.metadata.zip : "",
      productInformation,
      zipCodeValid: true,
      loadingProductInformation: false,
      loadingSubscriptionPurchase: false,
      addingPaymentMethod: initialPaymentMethod === "add",
      addPaymentMethodError: null,
      savingPaymentMethod: false,
      subscriptionCreationError: null,
      termsAndConditionsAccepted: false,
      csrfToken: document.querySelector('meta[name="csrf-token"]').content,
    }

    this.stripeElement = null
    this.setStripeElementRef = (element) => {
      this.stripeElement = element
    }
    this.handleAddPaymentMethod = this.handleAddPaymentMethod.bind(this)
    this.addPaymentMethodSuccess = this.addPaymentMethodSuccess.bind(this)
    this.addPaymentMethodError = this.addPaymentMethodError.bind(this)
    this.handlePaymentMethodChange = this.handlePaymentMethodChange.bind(this)
    this.handleZipCodeChange = this.handleZipCodeChange.bind(this)
    this.fetchProductInformation = this.fetchProductInformation.bind(this)
    this.checkZipCodeValidity = this.checkZipCodeValidity.bind(this)
    this.handlePurchase = this.handlePurchase.bind(this)
    this.handleCancel = this.handleCancel.bind(this)
    this.toggleValue = this.toggleValue.bind(this)
    this.isPresent = this.isPresent.bind(this)
    this.disablePurchase = this.disablePurchase.bind(this)
    this.debouncedFetchProductInformation = _.debounce(
      this.fetchProductInformation,
      1000
    )
  }

  componentDidMount() {
    this.fetchProductInformation(this.state.zipCode)

    const {
      productInformation: { dockwa_plus_external_id },
    } = this.state

    Amplitude.trackEvent("dockwa_plus:checkout_screen_viewed", {
      dockwa_plus_external_id,
    })
  }

  fetchProductInformation(zipCode) {
    const params = zipCode
      ? new URLSearchParams({ postal_code: zipCode }).toString()
      : ""
    const url = `/dockwa_plus/product_information?${params}`
    this.setState({ loadingProductInformation: true })

    fetch(url)
      .then((response) => response.json())
      .then((productInformation) => {
        if (productInformation.error) {
          this.setState({
            subscriptionCreationError: productInformation.error,
            loadingProductInformation: true,
          })
        } else {
          this.setState({
            productInformation,
            loadingProductInformation: false,
          })
        }
      })
      .catch((error) => {
        console.error(error)
      })
  }

  handlePaymentMethodChange(event) {
    const paymentMethod = this.findPaymentMethod(event.target.value)

    if (event.target.value === "add") {
      this.setState({
        addingPaymentMethod: true,
        selectedPaymentMethod: "add",
        zipCode: "",
        zipCodeValid: true,
      })
      return
    }

    this.setState({
      addingPaymentMethod: false,
      selectedPaymentMethod: event.target.value,
      zipCode: paymentMethod.metadata.zip || "",
      zipCodeValid: this.isPresent(paymentMethod.metadata.zip),
      subscriptionCreationError: null,
    })

    this.fetchProductInformation(paymentMethod.metadata.zip)
  }

  handleAddPaymentMethod() {
    this.setState({ savingPaymentMethod: true })
    this.stripeElement.createStripePaymentMethod()
  }

  capitalize(str) {
    const lower = str.toLowerCase()
    return str.charAt(0).toUpperCase() + lower.slice(1)
  }

  addPaymentMethodSuccess(response) {
    const {
      id,
      billing_details: {
        address: { postal_code },
      },
      card: { brand, exp_month, exp_year, last4 },
    } = response

    const newPaymentMethod = {
      id: null,
      type: "PaymentMethod::Card",
      stripePaymentMethodId: id,
      title: `${this.capitalize(brand)} ****${last4}`,
      subtitle: `expires ${exp_month}/${exp_year}`,
      isExpired: false,
      isEditable: false,
      metadata: {
        zip: postal_code,
      },
    }

    this.props.setGlobalState((prevState) => {
      const updatedState = {
        ...prevState,
        paymentMethods: [...prevState.paymentMethods, newPaymentMethod],
      }

      if (!isset(prevState.selectedPaymentMethod)) {
        updatedState.selectedPaymentMethod = id
      }

      return updatedState
    })

    this.setState({
      selectedPaymentMethod: response.id,
      addingPaymentMethod: false,
      savingPaymentMethod: false,
      loadingProductInformation: false,
      zipCode: postal_code,
    })

    this.fetchProductInformation(postal_code)
  }

  addPaymentMethodError(error) {
    console.error(error)
  }

  handleZipCodeChange(event) {
    this.setState({
      subscriptionCreationError: null,
      zipCode: event.target.value,
    })

    if (event.target.checkValidity()) {
      this.debouncedFetchProductInformation(event.target.value)
    }
  }

  checkZipCodeValidity(event) {
    this.setState({
      zipCodeValid: event.target.checkValidity(),
    })
  }

  findPaymentMethod(paymentMethodId) {
    return this.props.paymentMethods.find(
      (paymentMethod) => paymentMethod.stripePaymentMethodId === paymentMethodId
    )
  }

  toggleValue(value) {
    this.setState({ [value]: !this.state[value] })
  }

  isPresent(value) {
    return value !== null && value !== undefined && value !== ""
  }

  handlePurchase(_event) {
    const { selectedPaymentMethod, zipCode, productInformation } = this.state
    const { setGlobalState, goToNextScreen } = this.props
    this.setState({ loadingSubscriptionPurchase: true })

    const {
      productInformation: { dockwa_plus_external_id },
    } = this.state

    Amplitude.trackEvent("dockwa_plus:purchase_pressed", {
      dockwa_plus_external_id,
    })

    fetch("/account/dockwa_plus/subscriptions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.state.csrfToken,
      },
      body: JSON.stringify({
        dockwa_plus_version: productInformation.dockwa_plus_version,
        payment_method_id: selectedPaymentMethod,
        postal_code: zipCode,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then(({ error }) => {
            return Promise.reject(new Error(error))
          })
        }
        return response.json()
      })
      .then((_user) => {
        const {
          productInformation: { dockwa_plus_external_id },
        } = this.state

        Amplitude.trackEvent("dockwa_plus:membership_purchased", {
          dockwa_plus_external_id,
        })

        // Find the payment method object that was just used
        const selectedPaymentMethodObject = this.findPaymentMethod(
          selectedPaymentMethod
        )

        setGlobalState(
          (prevState) => ({
            ...prevState,
            ...{
              user: {
                ...prevState.user,
                activeDockwaPlusSubscription: true,
              },
              // Preserve the selected payment method for checkout
              selectedPaymentMethod: selectedPaymentMethodObject,
              paymentMethodCreated: "previously",
            },
          }),
          () => {
            goToNextScreen("confirmation")
          }
        )
      })
      .catch((error) => {
        const {
          productInformation: { dockwa_plus_external_id },
        } = this.state

        Amplitude.trackEvent("dockwa_plus:membership_purchase_failed", {
          dockwa_plus_external_id,
          error: error.message,
        })

        this.setState({
          loadingSubscriptionPurchase: false,
          subscriptionCreationError: error.message,
        })
      })
  }

  handleCancel() {
    const {
      productInformation: { dockwa_plus_external_id },
    } = this.state

    Amplitude.trackEvent("dockwa_plus:cancel_purchase_pressed", {
      dockwa_plus_external_id,
    })

    this.props.closeModal()
  }

  disablePurchase() {
    const {
      zipCodeValid,
      loadingSubscriptionPurchase,
      loadingProductInformation,
      termsAndConditionsAccepted,
      subscriptionCreationError,
    } = this.state

    return (
      loadingSubscriptionPurchase ||
      loadingProductInformation ||
      !termsAndConditionsAccepted ||
      !zipCodeValid ||
      !!subscriptionCreationError
    )
  }

  render() {
    const { paymentMethods } = this.props

    const {
      productInformation,
      selectedPaymentMethod,
      zipCode,
      zipCodeValid,
      loadingProductInformation,
      termsAndConditionsAccepted,
      subscriptionCreationError,
      addingPaymentMethod,
      savingPaymentMethod,
      loadingSubscriptionPurchase,
    } = this.state

    const selectedPaymentMethodObject = this.findPaymentMethod(
      selectedPaymentMethod
    )

    const benefits = [
      "Competitive Dockage Rates",
      "Unbeatable Fuel Prices",
      "Exclusive Premium Features: Cruise Plan Tool and more",
    ]

    return (
      <div>
        <h3 className="m-0 mb-8 font-bold text-gray-900">
          Sign up for Dockwa+
        </h3>
        <div className="flex flex-col gap-4 lg:flex-row lg:gap-0">
          {/* Left Panel */}
          <div
            className="w-full flex-none lg:mr-4 lg:w-[260px]"
            id="left-panel"
          >
            <div className="rounded-lg bg-gradient-to-r from-blue-100 to-blue-50 p-4 shadow-md">
              <span>
                {productInformation.marketing_text} With Dockwa+ you get access
                to:
              </span>
              <div className="mt-6 flex flex-col gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <i className="icon icon-check text-supplementary-green-600" />
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* Right Panel */}
          <div
            className="flex-1 lg:border-l lg:border-gray-200 lg:pl-4"
            id="right-panel"
          >
            <div className="flex items-start gap-4">
              <DockwaPlusCard className="w-[147px]" />
              <div className="flex flex-col gap-1">
                <span className="font-semibold">Dockwa+ Membership</span>
                <span className="text-gray-500">
                  Renews on{" "}
                  {moment(productInformation.renewal_date).format("ll")}
                </span>
                <span className="text-gray-500">
                  Discounts apply automatically
                </span>
              </div>
            </div>
            <div className="mt-4 flex gap-4 *:mb-0 *:flex-1">
              <FormGroup
                inputType="select"
                value={selectedPaymentMethod}
                name="paymentMethod"
                label="Payment Method"
                disabled={loadingSubscriptionPurchase}
                onChange={this.handlePaymentMethodChange}
              >
                {paymentMethods.map((paymentMethod) => (
                  <option
                    key={paymentMethod.id}
                    value={paymentMethod.stripePaymentMethodId}
                  >
                    {paymentMethod.title}
                  </option>
                ))}
                <option value="add">Add a new payment method</option>
              </FormGroup>
              <div className="*:mb-0">
                <FormGroup
                  value={zipCode}
                  name="zipCode"
                  label="Zip Code"
                  onChange={this.handleZipCodeChange}
                  pattern="^\d{5}$"
                  required={true}
                  onBlur={this.checkZipCodeValidity}
                  disabled={
                    (selectedPaymentMethodObject &&
                      this.isPresent(
                        selectedPaymentMethodObject.metadata.zip
                      )) ||
                    addingPaymentMethod ||
                    loadingProductInformation ||
                    loadingSubscriptionPurchase
                  }
                  customStyle={
                    zipCodeValid ? undefined : { borderColor: "#f87171" }
                  }
                />
                {zipCodeValid ? (
                  <span className="text-xs font-semibold text-gray-500">
                    5 digit format
                  </span>
                ) : (
                  <span className="text-xs font-semibold text-red-500">
                    5 digit format
                  </span>
                )}
              </div>
            </div>
            {addingPaymentMethod && (
              <div className="mt-4">
                <StripeElement
                  ref={this.setStripeElementRef}
                  successCallback={this.addPaymentMethodSuccess}
                  errorCallback={this.addPaymentMethodError}
                />
                <NextButton
                  id="addPaymentMethodContinueButton"
                  btnClass="btn-secondary"
                  processing={savingPaymentMethod}
                  click={this.handleAddPaymentMethod}
                  icon="icon-lock-fa"
                  text="Add Payment Method"
                  processingText="Adding Payment Method..."
                />
                <HorizontalRule />
              </div>
            )}
            <div className="mt-4">
              <LineItems
                lineItems={productInformation.line_items}
                loading={loadingProductInformation}
              />
            </div>
            <div className="mt-6 flex items-center gap-4 text-xs text-gray-600">
              <input
                className="m-0"
                type="checkbox"
                id="termsAndConditionsAccepted"
                name="termsAndConditionsAccepted"
                value={termsAndConditionsAccepted}
                onChange={() => this.toggleValue("termsAndConditionsAccepted")}
              />
              <label className="m-0" htmlFor="termsAndConditionsAccepted">
                I agree to{" "}
                <a
                  href="https://ahoy.dockwa.com/about-us/terms-of-service"
                  className="font-semibold text-blue-600"
                  rel="noreferrer noopener"
                  target="_blank"
                >
                  Dockwa&apos;s Terms and Conditions
                </a>{" "}
                and{" "}
                <a
                  href="https://ahoy.dockwa.com/privacy"
                  className="font-semibold text-blue-600"
                  rel="noreferrer noopener"
                  target="_blank"
                >
                  Privacy Policy
                </a>{" "}
                and consent to being charged annually, with automatic renewals
                until I cancel in my account settings. I understand that pricing
                may change, and no refunds will be provided for unused
                subscription periods.
              </label>
            </div>
            <div className="my-2 h-10">
              {subscriptionCreationError && (
                <div className="text-xs font-semibold text-red-500">
                  {subscriptionCreationError}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="btn btn-tertiary"
                data-dismiss="modal"
                aria-label="Close"
                disabled={loadingSubscriptionPurchase}
                onClick={this.handleCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={this.disablePurchase()}
                onClick={this.handlePurchase}
              >
                Purchase Dockwa+
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }
}

DockwaPlusPurchaseScreen.propTypes = {
  paymentMethods: PropTypes.array.isRequired,
  productInformation: PropTypes.shape({
    amount: PropTypes.number,
    dockwa_plus_external_id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    marketing_text: PropTypes.string.isRequired,
  }).isRequired,
  setGlobalState: PropTypes.func.isRequired,
  user: PropTypes.shape({
    id: PropTypes.number.isRequired,
    fullName: PropTypes.string,
  }).isRequired,
  goToNextScreen: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
}

export default DockwaPlusPurchaseScreen
